import React, { useState, useEffect } from "react";
import "./ProjectDecisionMaking.css";
import casesDataEn from "./cases-data-en";
import casesDataZh from "./cases-data-zh";
import ScenarioCard from "./ScenarioCard";
import LanguageSelector from "./LanguageSelector";

const ProjectDecisionMaking = () => {
  const [selectedLanguage, setSelectedLanguage] = useState("en");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [userDecisions, setUserDecisions] = useState({});
  const [userExplanations, setUserExplanations] = useState({});

  const currentData = selectedLanguage === "en" ? casesDataEn : casesDataZh;

  const handleLanguageChange = (language) => {
    setSelectedLanguage(language);
    setSelectedCategory(null);
    setSelectedScenario(null);
    setUserDecisions({});
    setUserExplanations({});
  };

  const handleCategorySelect = (categoryId) => {
    setSelectedCategory(categoryId);
    setSelectedScenario(null);
  };

  const handleScenarioSelect = (scenarioId) => {
    setSelectedScenario(scenarioId);
  };

  const handleDecisionChange = (scenarioId, decision) => {
    setUserDecisions((prev) => ({
      ...prev,
      [scenarioId]: decision,
    }));
  };

  const handleExplanationChange = (scenarioId, explanation) => {
    setUserExplanations((prev) => ({
      ...prev,
      [scenarioId]: explanation,
    }));
  };

  const getSelectedScenarioData = () => {
    if (!selectedCategory || !selectedScenario) return null;

    const category = currentData.categories.find(
      (cat) => cat.id === selectedCategory
    );
    if (!category) return null;

    return category.scenarios.find(
      (scenario) => scenario.id === selectedScenario
    );
  };

  const getProgressStats = () => {
    const totalScenarios = currentData.categories.reduce(
      (total, category) => total + category.scenarios.length,
      0
    );

    const completedDecisions = Object.keys(userDecisions).length;
    const completedExplanations = Object.keys(userExplanations).length;

    return {
      total: totalScenarios,
      decisions: completedDecisions,
      explanations: completedExplanations,
    };
  };

  const stats = getProgressStats();

  return (
    <div className="project-decision-making">
      <div className="header">
        <div className="header-content">
          <div className="title-section">
            <h1>
              {selectedLanguage === "zh"
                ? "AI决策制定框架"
                : "AI Decision Making Framework"}
            </h1>
            <p className="subtitle">
              {selectedLanguage === "zh"
                ? "人工智能时代的战略领导力"
                : "Strategic Leadership in the Age of Artificial Intelligence"}
            </p>
          </div>
          <LanguageSelector
            selectedLanguage={selectedLanguage}
            onLanguageChange={handleLanguageChange}
          />
        </div>
        <div className="progress-stats">
          <div className="stat">
            <span className="stat-number">{stats.decisions}</span>
            <span className="stat-label">
              {selectedLanguage === "zh" ? "战略决策" : "Strategic Decisions"}
            </span>
          </div>
          <div className="stat">
            <span className="stat-number">{stats.explanations}</span>
            <span className="stat-label">
              {selectedLanguage === "zh" ? "理由记录" : "Rationale Documented"}
            </span>
          </div>
          <div className="stat">
            <span className="stat-number">{stats.total}</span>
            <span className="stat-label">
              {selectedLanguage === "zh" ? "案例研究" : "Case Studies"}
            </span>
          </div>
        </div>
      </div>

      <div className="main-content">
        <div className="sidebar">
          <h2>
            {selectedLanguage === "zh"
              ? "案例研究分类"
              : "Case Study Categories"}
          </h2>
          {currentData.categories.map((category) => (
            <div key={category.id} className="category-section">
              <button
                className={`category-button ${
                  selectedCategory === category.id ? "active" : ""
                }`}
                onClick={() => handleCategorySelect(category.id)}
              >
                {category.title}
                <span className="scenario-count">
                  ({category.scenarios.length})
                </span>
              </button>

              {selectedCategory === category.id && (
                <div className="scenarios-list">
                  {category.scenarios.map((scenario) => (
                    <button
                      key={scenario.id}
                      className={`scenario-button ${
                        selectedScenario === scenario.id ? "active" : ""
                      }`}
                      onClick={() => handleScenarioSelect(scenario.id)}
                    >
                      {scenario.title}
                      {userDecisions[scenario.id] && (
                        <span className="decision-indicator">✓</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="content-area">
          {getSelectedScenarioData() ? (
            <ScenarioCard
              scenario={getSelectedScenarioData()}
              userDecision={userDecisions[selectedScenario]}
              userExplanation={userExplanations[selectedScenario]}
              onDecisionChange={(decision) =>
                handleDecisionChange(selectedScenario, decision)
              }
              onExplanationChange={(explanation) =>
                handleExplanationChange(selectedScenario, explanation)
              }
              language={selectedLanguage}
            />
          ) : (
            <div className="welcome-message">
              <h2>
                {selectedLanguage === "zh"
                  ? "高管决策制定实验室"
                  : "Executive Decision Making Laboratory"}
              </h2>
              <p>
                {selectedLanguage === "zh"
                  ? "探索复杂的AI驱动商业场景，培养战略决策制定能力。选择案例研究分类开始您的领导力之旅。"
                  : "Navigate complex AI-driven business scenarios and develop strategic decision-making capabilities. Select a case study category to begin your leadership journey."}
              </p>
              <div className="features">
                <div className="feature">
                  <h3>
                    {selectedLanguage === "zh"
                      ? "🎯 战略领导力"
                      : "🎯 Strategic Leadership"}
                  </h3>
                  <p>
                    {selectedLanguage === "zh"
                      ? "掌握AI集成商业环境中的决策制定框架"
                      : "Master decision-making frameworks for AI-integrated business environments"}
                  </p>
                </div>
                <div className="feature">
                  <h3>
                    {selectedLanguage === "zh"
                      ? "🧠 批判性分析"
                      : "🧠 Critical Analysis"}
                  </h3>
                  <p>
                    {selectedLanguage === "zh"
                      ? "通过人类判断和商业伦理的视角评估AI建议"
                      : "Evaluate AI recommendations through the lens of human judgment and business ethics"}
                  </p>
                </div>
                <div className="feature">
                  <h3>
                    {selectedLanguage === "zh"
                      ? "📊 高管文档记录"
                      : "📊 Executive Documentation"}
                  </h3>
                  <p>
                    {selectedLanguage === "zh"
                      ? "记录战略理由并建立决策制定专业知识的组合"
                      : "Document strategic rationale and build a portfolio of decision-making expertise"}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectDecisionMaking;
